import { captureException, captureMessage } from "@sentry/react";
import { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import jwt_decode, { JwtPayload } from "jwt-decode";
import ReactGA from "react-ga4";

import { removeUserFromLocalStorage } from "./privateRequests/user/logout";

const removeUserAndRedirectToLogin = () => {
  removeUserFromLocalStorage();
  window.location.href = "/login";
};

const decodeTokenAndCheckIfValidFormat = (token: string): JwtPayload => {
  try {
    return jwt_decode<JwtPayload>(token);
  } catch {
    removeUserAndRedirectToLogin();
  }
};

const checkIfTokenIsExpired = (token: JwtPayload): any => {
  if (token.exp * 1000 < new Date().getTime()) {
    //  This means that the access token has expired. Should redirect user to login and make sure user is properly logged out
    removeUserAndRedirectToLogin();
  }
};

const onRequest = async (config: any): Promise<any> => {
  let accessToken = JSON.parse(localStorage.getItem("access_token"));
  if (!accessToken) return config;
  const decodedToken = decodeTokenAndCheckIfValidFormat(accessToken);
  checkIfTokenIsExpired(decodedToken);

  config.headers["Authorization"] = `Bearer ${accessToken}`;
  config.headers["Platform"] =
    localStorage.getItem("platform") || "Playstation";
  return config;
};

const onResponse = (response: AxiosResponse): AxiosResponse => {
  return response;
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  return Promise.reject(error);
};

const onResponseError = (error: AxiosError): Promise<AxiosError> => {
  // Log 400 errors related to player-role-id being undefined
  const url = error.config?.url || "";
  const params = error.config?.params || {};
  if (error.response?.status === 401) {
    ReactGA.event({
      category: "Unauthorized",
      action: "Response_401_logging_out",
    });
    removeUserAndRedirectToLogin();
  }

  captureException(error, {
    tags: {
      error_type: error?.response?.status,
      endpoint: url.split("?")[0],
      from_interceptor: true,
    },
    extra: {
      url: url,
      params: params,
      request_config: error?.config,
      response_data: error.response?.data,
    },
  });

  return Promise.reject(error);
};

export const setupInterceptorsTo = (
  axiosInstance: AxiosInstance,
): AxiosInstance => {
  axiosInstance.interceptors.request.use(onRequest, onRequestError);
  axiosInstance.interceptors.response.use(onResponse, onResponseError);
  return axiosInstance;
};
